import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { MessageService } from 'primeng/api';
import { ListboxClickEvent, Listbox } from 'primeng/listbox';
import { from, map, take } from 'rxjs';
import { TaskService } from 'src/app/layout/topbar/components/tasks-sidebar/services/task.service';
import { EnvironmentService } from 'src/app/services/environment/environmentService.service';
import { RestService } from 'src/app/services/rest.service';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { RgsKeycloakProfile } from 'src/app/shared/models/rgs-keycloak-profile.model';
import { PermissionsService } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { ToastUtils } from 'src/app/shared/utils/toast.utils';
import { ProgressSpinner } from 'primeng/progressspinner';
import { UpperCasePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-environment-selector',
  templateUrl: './environment-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProgressSpinner, Listbox, UpperCasePipe, TranslateModule],
})
export class EnvironmentSelectorComponent {
  private readonly keycloack = inject(Keycloak);
  private readonly environmentService = inject(EnvironmentService);
  private readonly restService = inject(RestService);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly taskService = inject(TaskService);
  private readonly toastService = inject(MessageService);

  public licensesResource = rxResource({
    defaultValue: [],
    loader: () =>
      from(this.keycloack.loadUserProfile()).pipe(
        map((profile) => {
          const rgsProfile = profile as RgsKeycloakProfile;
          return ArrayUtils.ensureArray(rgsProfile?.attributes?.licentie)
            .sort((a, b) => a.localeCompare(b))
            .map((license) => ({ label: license, value: license }));
        }),
      ),
  });

  public loading = signal(false);

  public licenseClicked(event: ListboxClickEvent) {
    const license = event.option.value;

    this.loading.set(true);
    this.restService
      .post('/licensecontroller', { company: license }, 'text')
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.taskService.unloadItems();
          window.sessionStorage.setItem('environment', license);
          this.permissionsService.license$.next(license);

          const keycloakProfile: RgsKeycloakProfile = this.keycloack.profile as RgsKeycloakProfile;

          if (keycloakProfile && keycloakProfile.id) {
            this.permissionsService.loadUser(keycloakProfile.id);
          } else {
            console.error('Keycloak profile is not available or does not contain an ID.');
          }

          const oldUrl = this.environmentService.oldUrl;
          if (oldUrl && oldUrl !== '' && oldUrl !== 'environment' && oldUrl !== 'environment-selector') {
            const baseUrl = window.location.origin;
            if (oldUrl.startsWith(baseUrl)) {
              const relativeUrl = oldUrl.substring(baseUrl.length);
              this.router.navigateByUrl(relativeUrl);
            } else {
              this.router.navigateByUrl(oldUrl);
            }
          } else {
            this.router.navigateByUrl('');
          }
        },
        error: (err: RgsHttpErrorResponse) => {
          ToastUtils.showError(this.toastService, null, err.error);
        },
        complete: () => {
          this.loading.set(false);
        },
      });
  }

  public logout() {
    window.sessionStorage.removeItem('environment');
    window.sessionStorage.removeItem('tenant');

    this.loading.set(true);
    this.keycloack
      .logout()
      .then(() => {
        this.loading.set(false);
      })
      .catch(() => {
        this.loading.set(false);
      });
  }
}
